import { CircularProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import { useDebouncedCallback } from '../../../../../../hooks'
import ComponentDocsPanel from '../../ComponentDocsPanel'
import AgentSidePanel from '../../AgentSidePanel'
import ComparePane from '../ComparePane'
import CodeEditorToolbar from './CodeEditorToolbar'
import ContentCodeView from './ContentCodeView'
import ContentPreviewView from './ContentPreviewView'
import ViewContentHeaderControls from './ViewContentHeaderControls'

const ViewContentContainer = forwardRef(({
  jsxContent,
  viewId,
  onJsxContentChange,
  onDiagnosticsChange: onDiagnosticsChangeProp,
  toolbarLeft,
  isEdit,
  isComparing,
  onVersionCreated
}, ref) => {
  const codeViewRef = useRef(null)

  const [viewMode, setViewMode] = useState('code')

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode)
    if (mode === 'code') {
      setTimeout(() => {
        codeViewRef.current?.focus?.()
      }, 100)
    }
  }, [])

  const handleFind = useCallback(() => {
    codeViewRef.current?.find?.()
  }, [])

  const handleReplace = useCallback(() => {
    codeViewRef.current?.replace?.()
  }, [])

  const handleUndo = useCallback(() => {
    codeViewRef.current?.undo?.()
  }, [])

  const handleRedo = useCallback(() => {
    codeViewRef.current?.redo?.()
  }, [])

  const handleFormat = useCallback(async () => {
    await codeViewRef.current?.format?.()
  }, [])

  const views = [
    { id: 'code', title: 'Code View', icon: 'file', size: 'sm' },
    { id: 'preview', title: 'Preview', icon: 'visibility', size: 'sm' }
  ]

  const [docsComponentName, setDocsComponentName] = useState(null)
  const [aiDesignerActive, setAiDesignerActive] = useState(false)
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false)
  const [persistedThreadId, setPersistedThreadId] = useState(null)
  const [previewContent, setPreviewContent] = useState(jsxContent)
  const [editorDiagnostics, setEditorDiagnostics] = useState([])

  useImperativeHandle(ref, () => ({
    getEditorValue: () => codeViewRef.current?.getValue?.()
  }))

  const handleDiagnosticsChange = useCallback((diagnostics) => {
    setEditorDiagnostics(diagnostics)
    onDiagnosticsChangeProp?.(diagnostics)
  }, [onDiagnosticsChangeProp])

  const debouncedFormUpdate = useDebouncedCallback(
    useCallback((jsx) => {
      onJsxContentChange(jsx)
    }, [onJsxContentChange]),
    500
  )

  const handleJsxContentChange = useCallback((jsx) => {
    setPreviewContent(jsx)
    debouncedFormUpdate(jsx)
  }, [debouncedFormUpdate])

  const handleJsxUpdate = (jsx) => {
    if (onJsxContentChange) {
      onJsxContentChange(jsx)
      setPreviewContent(jsx)
      setViewMode('code')
      setTimeout(() => {
        setViewMode('preview')
      }, 100)
    }
    setIsLoadingAttachment(false)
  }

  const hasSidePanel = aiDesignerActive || docsComponentName
  const contentWidth = hasSidePanel ? 'calc(100% - 410px)' : '100%'

  const editorContent = (
    <div
      className='relative flex-1 flex flex-col'
      style={{
        overflow: 'hidden',
        border: '5px solid var(--color-neutral-300)',
        borderRadius: 'var(--border-radius-lg)'
      }}
    >
      {isComparing && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--dim-2)',
            padding: 'var(--dim-1) var(--dim-2)',
            borderBottom: '1px solid var(--color-neutral-200)',
            background: 'var(--color-neutral-050)',
            flexShrink: 0
          }}
        >
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 'var(--font-weight-bold)',
              textTransform: 'uppercase',
              color: 'var(--color-neutral-500)',
              letterSpacing: '0.05em'
            }}
          >
            Current Version
          </span>
        </div>
      )}
      <div className='relative h-full w-full flex flex-col' style={{ display: viewMode === 'code' ? 'flex' : 'none' }}>
        <CodeEditorToolbar
          diagnostics={editorDiagnostics}
          onFind={handleFind}
          onReplace={handleReplace}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onFormat={handleFormat}
        />
        <div className='flex-1' style={{ minHeight: 0 }}>
          <ContentCodeView
            ref={codeViewRef}
            jsxContent={jsxContent}
            onJsxContentChange={handleJsxContentChange}
            onDiagnosticsChange={handleDiagnosticsChange}
            onComponentClick={setDocsComponentName}
          />
        </div>
        {isLoadingAttachment && (
          <div
            className='absolute flex items-center justify-center'
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10, top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div
              className='flex items-center gap-2 px-4 py-2 rounded'
              style={{ backgroundColor: 'var(--color-ai-100)', color: 'var(--color-ai-700)', border: '1px solid var(--color-ai-300)' }}
            >
              <CircularProgress color='inherit' size='18px' />
              Loading attachment...
            </div>
          </div>
        )}
      </div>

      <div className='relative h-full w-full' style={{ display: viewMode === 'preview' ? 'block' : 'none', overflow: 'auto' }}>
        {viewMode === 'preview' && <ContentPreviewView jsxContent={previewContent} />}
        {isLoadingAttachment && (
          <div
            className='absolute flex items-center justify-center'
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10, top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div
              className='flex items-center gap-2 px-4 py-2 rounded'
              style={{ backgroundColor: 'var(--color-ai-100)', color: 'var(--color-ai-700)', border: '1px solid var(--color-ai-300)' }}
            >
              <CircularProgress color='inherit' size='18px' />
              Loading attachment...
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className='h-full w-full flex flex-col gap-md relative'>
      <div className='flex gap-md justify-between items-center'>
        {toolbarLeft}
        <ViewContentHeaderControls
          onOpenDesigner={() => setAiDesignerActive(true)}
          views={views}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </div>

      <div
        className='flex-1 flex gap-md'
        style={{
          overflow: 'hidden',
          width: hasSidePanel ? contentWidth : '100%',
          transition: 'width 0.3s ease-in-out'
        }}
      >
        {isComparing && <ComparePane viewId={viewId} viewMode={viewMode} />}
        {editorContent}
      </div>

      {docsComponentName && !aiDesignerActive && (
        <div
          style={{
            position: 'absolute',
            top: '80px',
            right: 0,
            bottom: 0,
            width: '400px',
            height: 'auto',
            background: 'white',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          <ComponentDocsPanel
            componentName={docsComponentName}
            onClose={() => setDocsComponentName(null)}
          />
        </div>
      )}

      {aiDesignerActive && (
        <AgentSidePanel
          agentId={isEdit ? 'ae-view-editor' : 'ae-view-builder'}
          onClose={() => setAiDesignerActive(false)}
          onJsxUpdate={handleJsxUpdate}
          onAttachmentLoadStart={() => setIsLoadingAttachment(true)}
          onThreadIdChange={setPersistedThreadId}
          onVersionCreated={onVersionCreated}
          initialThreadId={persistedThreadId}
          viewContext={isEdit ? { viewId, content: jsxContent } : undefined}
        />
      )}
    </div>
  )
})

ViewContentContainer.displayName = 'ViewContentContainer'

ViewContentContainer.propTypes = {
  jsxContent: PropTypes.string,
  viewId: PropTypes.number,
  onJsxContentChange: PropTypes.func,
  onDiagnosticsChange: PropTypes.func,
  toolbarLeft: PropTypes.node,
  isEdit: PropTypes.bool,
  isComparing: PropTypes.bool,
  onVersionCreated: PropTypes.func
}

export default ViewContentContainer
